import { Directive, ElementRef, Input, OnChanges, OnDestroy } from "@angular/core";
import { HttpBackend, HttpClient } from "@angular/common/http";
import { Observable, Subscription, shareReplay } from "rxjs";

const NS = "http://www.w3.org/2000/svg";
const DRAWABLE = /^(path|g|rect|circle|ellipse|line|polyline|polygon|use|image|text)$/;

const cache = new Map<string, Observable<string>>(); // one request per URL
let uid = 0; // unique blur ids across all icons on the page

/** Small SVG element factory. */
const mk = (tag: string, attrs: Record<string, string | number> = {}, kids: Node[] = []): Element => {
    const el = document.createElementNS(NS, tag);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, String(v)));
    el.append(...kids);
    return el;
};

/**
 * Replaces Figma's background blur (`foreignObject` + `backdrop-filter`) with a
 * pure SVG blur (mask + clip + feGaussianBlur) that renders on every device.
 * Layers that don't match Figma's pattern are left untouched.
 */
function fixBlur(root: SVGSVGElement | null): void {
    const fos = Array.from(root?.querySelectorAll("foreignObject") ?? []);
    if (!root || !fos.length) return;

    // Large region (SVG user units) for masks and filters.
    const { x, y, width, height } = root.viewBox.baseVal;
    if (!width || !height) return; // no viewBox -> can't size the mask/filter safely
    const region = { x: x - width, y: y - height, width: width * 3, height: height * 3 };

    for (const fo of fos) {
        const parent = fo.parentElement!;
        const style = fo.querySelector("div")?.getAttribute("style") ?? "";
        const blur = /backdrop-filter:\s*blur\(\s*([\d.]+)/i.exec(style);
        const clipId = /clip-path:\s*url\(\s*['"]?#([^'")\s]+)/i.exec(style)?.[1];
        const orig = clipId ? root.querySelector(`#${CSS.escape(clipId)}`) : null;
        const siblings = Array.from(parent.children);

        // Shapes drawn BEFORE the foreignObject are the ones it blurs.
        const below = siblings.slice(0, siblings.indexOf(fo)).filter((c) => DRAWABLE.test(c.localName));
        if (!blur || !orig || !below.length) continue; // not a Figma blur layer

        const id = `blur-${++uid}`;
        const u = (s: string) => `url(#${id}-${s})`;

        // Figma's clipPath is relative to the foreignObject's top-left,
        // so re-anchor it to SVG user space with translate(x y).
        const [fx, fy] = ["x", "y"].map((a) => fo.getAttribute(a) || "0");
        const clip = mk(
            "clipPath",
            { id: `${id}-c`, transform: `translate(${fx} ${fy}) ${orig.getAttribute("transform") ?? ""}` },
            Array.from(orig.children, (c) => c.cloneNode(true))
        );

        // Mask: white everywhere, black inside the glass (hides the sharp shapes there).
        const mask = mk("mask", { id: `${id}-m`, maskUnits: "userSpaceOnUse", ...region }, [
            mk("rect", { ...region, fill: "#fff" }),
            mk("g", { "clip-path": u("c") }, [mk("rect", { ...region, fill: "#000" })]),
        ]);

        // CSS blur(Npx) == feGaussianBlur stdDeviation N.
        const filter = mk("filter", { id: `${id}-f`, filterUnits: "userSpaceOnUse", ...region }, [
            mk("feGaussianBlur", { stdDeviation: blur[1] }),
        ]);

        // Add next to the original clipPath (works wherever the optimizer put it).
        orig.parentElement!.append(clip, mask, filter);

        // Blurred copies (ids stripped to avoid duplicates), taken before the originals move.
        const copies = below.map((n) => {
            const c = n.cloneNode(true) as Element;
            [c, ...Array.from(c.querySelectorAll("[id]"))].forEach((e) => e.removeAttribute("id"));
            return c;
        });

        // Originals -> masked wrapper; foreignObject -> blurred copy clipped to the glass.
        const sharp = mk("g", { mask: u("m") });
        parent.insertBefore(sharp, below[0]);
        sharp.append(...below);
        fo.replaceWith(mk("g", { "clip-path": u("c") }, [mk("g", { filter: u("f") }, copies)]));
    }
}

/**
 * Renders an icon into the host element.
 *
 * - `.svg` sources (optional ?query/#hash) are fetched and inlined, because
 *   `<img>` strips `foreignObject` / `backdrop-filter`. Figma's background blur
 *   is rewritten to a pure SVG blur on every device (see `fixBlur`).
 * - Everything else (png/jpg/webp/...) is rendered through an `<img>`.
 * - Safe fallbacks: SSR is skipped, a failing blur fix keeps the original SVG,
 *   and a failed SVG fetch or broken raster leaves the host empty.
 *
 * Usage:
 *   <span svgIcon="assets/svg/icons/upskilling-gb.svg"></span>
 *   <span svgIcon="assets/img/icons/badge.webp"></span>
 */
@Directive({ selector: "[svgIcon]" })
export class SvgIconDirective implements OnChanges, OnDestroy {
    @Input("svgIcon") src?: string | null;

    private readonly http: HttpClient;
    private sub?: Subscription;

    constructor(
        private readonly el: ElementRef<HTMLElement>,
        handler: HttpBackend
    ) {
        // Skips AuthInterceptor (it calls `new URL()` on relative paths and throws).
        this.http = new HttpClient(handler);
    }

    ngOnChanges(): void {
        if (typeof document === "undefined") return; // SSR: no DOM available

        this.sub?.unsubscribe(); // cancels any pending request for the previous src
        const src = this.src?.trim();
        const host = this.el.nativeElement;

        if (!src) return host.replaceChildren();

        if (!/\.svg(\?|#|$)/i.test(src)) {
            const img = new Image();
            img.alt = "";
            img.onerror = () => host.replaceChildren(); // no broken-image icon
            img.src = src;
            return host.replaceChildren(img);
        }

        let req = cache.get(src);
        if (!req) cache.set(src, (req = this.http.get(src, { responseType: "text" }).pipe(shareReplay(1))));

        this.sub = req.subscribe({
            next: (svg) => {
                host.innerHTML = svg;
                try {
                    fixBlur(host.querySelector("svg"));
                } catch {
                    host.innerHTML = svg; // never break an icon because of the fix
                }
            },
            error: () => {
                cache.delete(src); // allow retry
                host.replaceChildren();
            },
        });
    }

    ngOnDestroy(): void {
        this.sub?.unsubscribe();
    }
}
